import Button from '@portalterreno/react-ui-core/Button';
import Typography from '@portalterreno/react-ui-core/Typography';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { setStatus } from '../../../redux/slices/contactForm';
import { RootState } from '../../../redux/store';
import SellerInformation from '../../SellerInformation';
import Divider from '../Divider';
import Content from './Container';
import ErrorMessage from './ErrorMessage';
import StyledModal from './StyledModal';
import SuccessMessage from './SuccessMessage';

export type SentModalProps = React.PropsWithChildren<{
  companyLogo?: string;
  headerTitle?: string;
  isOpen: boolean;
  messageI18nKey?: string;
  secondaryMessageI18nKey?: string;
  sellerName: string;
  sellerPhone?: string;
  titleI18nKey?: string;
}>;

const SentModal: React.FC<SentModalProps> = ({
  children,
  companyLogo,
  headerTitle,
  isOpen,
  messageI18nKey,
  secondaryMessageI18nKey,
  sellerName,
  titleI18nKey,
  ...rest
}) => {
  const dispatch = useDispatch();

  const state = useSelector(
    (state: RootState) => ({
      status: state.contactForm.status,
      source: state.contactForm.source,
      isLeadLimitReached: state.contactForm.isLeadLimitReached
    }),
    shallowEqual
  );

  const handleOnClose = React.useCallback(() => {
    dispatch(setStatus('initial'));
  }, [dispatch]);

  const { t } = useTranslation('property');

  let secondaryMessage = t('contactFormBrokerWillContactYou', { sellerName });
  if (secondaryMessageI18nKey) {
    secondaryMessage = t(secondaryMessageI18nKey);
  }

  if (state.status === 'error') {
    secondaryMessage = t('contactFormTryAgainLater');
  }

  if (state.isLeadLimitReached) {
    secondaryMessage = 'Limite de contactos alcanzado.';
  }

  return (
    <StyledModal
      isOpen={isOpen || state.isLeadLimitReached}
      onBackdropClick={handleOnClose}
      title={headerTitle}
      footer={
        <Button
          color='midnight'
          fullWidth
          onClick={handleOnClose}
        >
          {t('close')}
        </Button>
      }
      onClose={handleOnClose}
      {...rest}
    >
      {headerTitle && <Divider />}
      <Content>
        {state.status === 'success' && (
          <SuccessMessage
            title={titleI18nKey}
            message={messageI18nKey}
          />
        )}
        {(state.status === 'error' || state.isLeadLimitReached) && (
          <ErrorMessage />
        )}

        <Typography weight='light'>{secondaryMessage}</Typography>
      </Content>
      {companyLogo && sellerName && (
        <SellerInformation
          sellerName={sellerName}
          sellerLogo={companyLogo}
        />
      )}
      {children}
    </StyledModal>
  );
};

export default SentModal;
